import React from "react";
import TimelineBooking from "./TimelineBooking";

interface WeeklyTimelineProps {
  data: {
    services: any[]; // The `days` array from your API response
    selectedDate: Date;
  };

  handlers: {
    onServiceClick: (service: any) => void;
    onShowMore: (selectedDate: Date, services: any[]) => void;
    onEmptySlotClick?: (time: string, dateStr: string) => void;
  };
}

// Format Date YYYY-MM-DD
const formatLocalDateISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Generate 7 days of the week starting from Monday
const getWeekDays = (selectedDate: Date) => {
  const monday = new Date(selectedDate);
  const day = monday.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diff);

  return Array.from({ length: 7 }, (_, index) => {
    const current = new Date(monday);
    current.setDate(monday.getDate() + index);
    return current;
  });
};

// Parse time string "13:00:00" or "13:00" to integer hour (e.g., 13)
const getHour = (time?: string): number => {
  if (!time) return 0;
  const hour = parseInt(time.split(":")[0], 10);
  return isNaN(hour) ? 0 : hour;
};

const WeeklyTimeline: React.FC<WeeklyTimelineProps> = ({ data, handlers }) => {
  const { services = [], selectedDate } = data;
  const weekDays = getWeekDays(selectedDate);

  // 1. Calculate dynamic min start hour and max end hour across the days array
  let minStartHour = 8;
  let maxEndHour = 18;

  if (services.length > 0) {
    const startHours = services
      .map((d: any) => getHour(d.workingStartTime))
      .filter((h) => h > 0);
    const endHours = services
      .map((d: any) => getHour(d.workingEndTime))
      .filter((h) => h > 0);

    if (startHours.length > 0) minStartHour = Math.min(...startHours);
    if (endHours.length > 0) maxEndHour = Math.max(...endHours);
  }

  // Generate dynamic array of hours for the grid
  const totalSlots = Math.max(1, maxEndHour - minStartHour + 1);
  const hours = Array.from({ length: totalSlots }, (_, i) => i + minStartHour);

  return (
    <div className="overflow-auto rounded-2xl border border-slate-200 bg-white shadow-xs select-none">
      {/* ---------------------------------------------------- */}
      {/* 1. WEEK HEADER                                       */}
      {/* ---------------------------------------------------- */}
      <div className="grid grid-cols-[90px_repeat(7,minmax(180px,1fr))] border-b bg-slate-50 font-nunito">
        <div className="flex items-center justify-center p-3 text-xs font-bold text-slate-400 border-r">
          TIME
        </div>

        {weekDays.map((day) => {
          const dayStr = formatLocalDateISO(day);
          const apiDay = services.find((d: any) => d.date === dayStr);
          const isOffDay = apiDay && apiDay.isWorkingDay === false;
          const workingStart = apiDay?.workingStartTime ? apiDay.workingStartTime.substring(0, 5) : "";
          const workingEnd = apiDay?.workingEndTime ? apiDay.workingEndTime.substring(0, 5) : "";

          return (
            <div
              key={day.toISOString()}
              className={`border-l border-slate-200 py-3 text-center ${
                isOffDay ? "bg-rose-50/70" : ""
              }`}
            >
              <p className={`font-extrabold text-sm ${isOffDay ? "text-rose-700" : "text-slate-800"}`}>
                {day.toLocaleDateString("default", { weekday: "short" })}
              </p>

              <p className="mt-0.5 text-xs text-slate-500 font-semibold">
                {day.getDate()}{" "}
                {day.toLocaleDateString("default", { month: "short" })}
              </p>

              {/* Working Hours Badge per Day */}
              {apiDay && isOffDay ? (
                <span className="inline-block mt-1 text-[10px] font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                  Off Day
                </span>
              ) : workingStart && workingEnd ? (
                <span className="inline-block mt-1 text-[10px] font-extrabold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                  {workingStart} - {workingEnd}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* ---------------------------------------------------- */}
      {/* 2. TIMELINE BODY GRID                                */}
      {/* ---------------------------------------------------- */}
      {hours.map((hour) => {
        const timeFormatted = hour >= 12 ? `${hour === 12 ? 12 : hour - 12}:00 PM` : `${hour}:00 AM`;
        const slotTime24 = `${hour < 10 ? "0" : ""}${hour}:00`;

        return (
          <div
            key={hour}
            className="grid grid-cols-[90px_repeat(7,minmax(180px,1fr))] border-b last:border-b-0 min-h-[140px]"
          >
            {/* Hour Label */}
            <div className="border-r bg-slate-50/50 pt-3 text-center text-xs font-black text-slate-600">
              {timeFormatted}
            </div>

            {/* Days Columns */}
            {weekDays.map((day) => {
              const dayStr = formatLocalDateISO(day);

              const apiDay = services.find((d: any) => d.date === dayStr);
              const isOffDay = apiDay && apiDay.isWorkingDay === false;
              const dayBookings = apiDay?.bookings || [];

              // Filter bookings starting in this hour slot
              const slotBookings = dayBookings.filter((service: any) => {
                const serviceHour = getHour(service.startTime);
                return serviceHour === hour;
              });

              return (
                <div
                  key={day.toISOString()}
                  onClick={(e) => {
                    if (isOffDay) return;
                    if (e.target === e.currentTarget && handlers.onEmptySlotClick) {
                      handlers.onEmptySlotClick(slotTime24, dayStr);
                    }
                  }}
                  className={`border-l border-slate-100 p-2 flex flex-col gap-2 items-stretch transition-colors ${
                    isOffDay
                      ? "bg-rose-50/30 cursor-not-allowed"
                      : "hover:bg-amber-50/30 cursor-pointer"
                  }`}
                >
                  {isOffDay ? (
                    <div className="h-full flex items-center justify-center text-[11px] font-bold text-rose-400 italic">
                      Closed
                    </div>
                  ) : (
                    <>
                      {/* Booking Cards */}
                      {slotBookings.slice(0, 2).map((service: any) => (
                        <TimelineBooking
                          key={service.bookingId || service.id}
                          data={{
                            service,
                            totalBookings: slotBookings.length,
                          }}
                          handlers={{
                            onClick: handlers.onServiceClick,
                          }}
                        />
                      ))}

                      {/* Show More Badge if > 2 Bookings in single slot */}
                      {slotBookings.length > 2 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlers.onShowMore(day, slotBookings);
                          }}
                          className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 transition-all hover:bg-amber-500 hover:text-white cursor-pointer"
                          title="Show More Services"
                        >
                          +{slotBookings.length - 2} More
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default WeeklyTimeline;